/**
 * Veeso module - API client for editor-page
 */

import type { Client } from '../../types';
import { apiBuilder } from '../../api-builder';
import { assetAPIDefinitions } from './asset';
import { authAPIDefinitions, getGoogleOAuthURL, getGoogleOAuthCallbackURL } from './auth';
import { billingAPIDefinitions } from './billing';
import { userAPIDefinitions } from './user';
import { projectAPIDefinitions } from './project';
import { llmAPIDefinitions } from './llm';
import { unsplashAPIDefinitions } from './unsplash';
import type { VeesoConfig, Asset } from './types';
import { createClient } from '../../client';

// Re-export types
export type {
  // Asset types
  Asset,
  AssetMeta,
  CreateAssetRequest,
  CreateAssetResponse,
  UpdateAssetRequest,
  GetAssetsParams,
  RemoveBackgroundRequest,
  RemoveBackgroundResponse,
  UpscaleImageRequest,
  ImageGenerateRequest,
  ImageGenerateResponse,
  GenerateImageWithRefsRequest,
  ImageEditV2Request,
  ImageGenerateV2Request,
  ImageJobResponse,
  ImageToolV2Request,
  JobStatus,
  VeesoConfig,
  OSSUploadMeta,
  UploadProgress,
  UploadProgressCallback,
  // Auth types
  SendEmailVerificationRequest,
  SendEmailVerificationResponse,
  CheckCaptchaRequest,
  CheckCaptchaResponse,
  SendSmsCodeRequest,
  SendSmsCodeResponse,
  SignInRequest,
  SignInEmailRequest,
  SignInPhoneRequest,
  SignInResponse,
  SignUpRequest,
  SignUpEmailRequest,
  SignUpPhoneRequest,
  SignUpResponse,
  AuthErrorResponse,
  // Billing types
  UsageResponse,
  UsageRecordsParams,
  UsageRecord,
  UsageRecordsResponse,
  DailyCreditsStatusResponse,
  ClaimDailyCreditsResponse,
  OrderParams,
  Order,
  OrdersResponse,
  InvoiceData,
  Invoice,
  InvoiceResponse,
  InvoicesResponse,
  // User types
  UserInfo,
  UpdateUserRequest,
  UpdateUserResponse,
  // Project types
  ProjectVisibility,
  ProjectMeta,
  CompressedData,
  ProjectData,
  Project,
  GetProjectsParams,
  GetRecentProjectsParams,
  GetTrashedProjectsParams,
  CreateProjectRequest,
  UpdateProjectRequest,
  StoreProjectRequest,
  StoreProjectResponse,
  FinalizeProjectRequest,
  FinalizeProjectResponse,
  UpdateProjectThumbnailRequest,
  GetProjectParams,
  DeleteProjectParams,
  ProjectInteractionRequest,
  ProjectInteractionResponse,
  // Template types
  TemplateSize,
  TemplateMeta,
  Template,
  CreateTemplateRequest,
  GetTemplatesByTagParams,
  // LLM types
  ChatMessage,
  ChatMessageContent,
  LLMGenerateRequest,
  LLMGenerateResponse,
  // Unsplash types
  UnsplashUser,
  UnsplashImageUrls,
  UnsplashImage,
  UnsplashSearchParams,
  UnsplashSearchResponse,
} from './types';

// Re-export API definitions for direct use
export { assetAPIDefinitions } from './asset';
export { authAPIDefinitions, getGoogleOAuthURL, getGoogleOAuthCallbackURL } from './auth';
export { billingAPIDefinitions } from './billing';
export { userAPIDefinitions } from './user';
export { projectAPIDefinitions } from './project';
export { llmAPIDefinitions } from './llm';
export { unsplashAPIDefinitions } from './unsplash';

/**
 * Create Veeso API instance
 *
 * @param client - API client instance
 * @param config - Veeso configuration
 * @returns Veeso API object with typed methods
 *
 * @example
 * ```typescript
 * import { createClient } from '@seede/api-client';
 * import { createVeesoAPI } from '@seede/api-client/veeso';
 *
 * const veeso = createVeesoAPI({
 *   baseURL: 'https://api.veeso.ai',
 *   staticBaseURL: 'https://static.veeso.ai',
 * });
 *
 * // Use the API
 * const assets = await veeso.api.getAssets({ source: 'import', limit: 32 });
 * ```
 */
export function createVeesoAPI(config: VeesoConfig & Parameters<typeof createClient>[0]) {
  const client = createClient(config);
  const api = apiBuilder(client, {
    ...assetAPIDefinitions,
    ...authAPIDefinitions,
    ...billingAPIDefinitions,
    ...userAPIDefinitions,
    ...projectAPIDefinitions,
    ...llmAPIDefinitions,
    ...unsplashAPIDefinitions,
  });

  function getAssetURL(asset: Asset | string): string {
    let url = typeof asset === 'string' ? asset : asset.url;

    if (url.startsWith('asset/') || url.startsWith('/asset/')) {
      return new URL(url, config.staticBaseURL).href;
    } else if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    } else {
      const assetId = url.startsWith('asset://') ? url.split('://')[1] : url;
      return new URL(`/asset/${assetId}`, config.staticBaseURL).href;
    }
  }

  /**
   * Normalize asset with full URL
   */
  function normalizeAsset(asset: Asset): Asset {
    return {
      ...asset,
      url: getAssetURL(asset),
      createdAt: asset.created_at ? new Date(asset.created_at).getTime() : undefined,
      updatedAt: asset.updated_at ? new Date(asset.updated_at).getTime() : undefined,
    };
  }

  /**
   * Fetch remote resource with authentication
   * @param url - Resource URL (absolute or relative to staticBaseURL)
   * @param options - Fetch options
   * @returns Parsed response based on type option
   */
  async function fetchResource<T = any>(
    url: string,
    options?: {
      /** Response type (default: 'json') */
      type?: 'json' | 'blob' | 'arrayBuffer' | 'text';
      withAuth?: boolean;
    }
  ): Promise<T> {
    // Resolve URL
    let fullUrl: string;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      fullUrl = url;
    } else {
      fullUrl = new URL(url, config.staticBaseURL).href;
    }

    // Build headers with auth token
    const headers: HeadersInit = {};
    const token = config.getToken?.();
    if (token && options?.withAuth !== false) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Fetch resource
    const response = await fetch(fullUrl, { headers });

    // Handle 401 unauthorized
    if (response.status === 401 && config.onUnauthorized) {
      config.onUnauthorized();
    }

    // Handle error responses
    if (!response.ok) {
      throw new Error(`Failed to fetch resource: ${response.status} ${response.statusText}`);
    }

    // Parse response based on type
    const type = options?.type ?? 'json';
    switch (type) {
      case 'blob':
        return response.blob() as Promise<T>;
      case 'arrayBuffer':
        return response.arrayBuffer() as Promise<T>;
      case 'text':
        return response.text() as Promise<T>;
      case 'json':
      default:
        return response.json() as Promise<T>;
    }
  }

  return {
    /**
     * Raw API methods
     */
    api,

    /**
     * Configuration
     */
    config,

    /**
     * Get the full URL for an asset
     * @param asset - Asset object, asset URL, asset id or absolute URL
     *
     * @example
     * ```
     * getAssetURL('/asset/123123123')
     * getAssetURL('asset/123123123)
     * getAssetURL({ url: '/asset/123123123' })
     * getAssetURL('asset://123123123')
     * getAssetURL('123123123')
     * getAssetURL('https://example.com/asset/123123123')
     * getAssetURL('data:image/png;....')
     * ```
     */
    getAssetURL,

    /**
     * Normalize asset with full URL
     */
    normalizeAsset,

    /**
     * Fetch remote resource with authentication
     * Supports JSON, Blob, ArrayBuffer, and text responses
     *
     * @example
     * ```typescript
     * // Fetch JSON
     * const data = await veeso.fetchResource('/project/123/data.json');
     *
     * // Fetch binary
     * const blob = await veeso.fetchResource('/asset/image.png', { type: 'blob' });
     * ```
     */
    fetchResource,

    /**
     * Get the static base URL
     */
    get staticBaseURL() {
      return config.staticBaseURL;
    },

    /**
     * Get Google OAuth URL for sign in
     * @param inviteCode - Optional invite code
     * @returns Google OAuth URL
     */
    getGoogleOAuthURL(inviteCode?: string) {
      return getGoogleOAuthURL(config.baseURL, inviteCode);
    },

    /**
     * Get Google OAuth callback URL
     * @param inviteCode - Optional invite code
     * @returns Google OAuth callback URL
     */
    getGoogleOAuthCallbackURL(inviteCode?: string) {
      return getGoogleOAuthCallbackURL(config.baseURL, inviteCode);
    },
  };
}

/**
 * Veeso API instance type
 */
export type VeesoAPI = ReturnType<typeof createVeesoAPI>;
